/**
 * client-side backfill: push the browser-only user state that exists ONLY in
 * localStorage today into the server's Mongo `user_data` hub. Called on login and
 * on app mount when a token is present. The server merges per-namespace, so this
 * is safe to run repeatedly. Failures are non-fatal (best-effort migration).
 */
import { API_URL } from '../config/env';
import { bookmarkStorageKey } from './studioBookmarks';
import { collectQuestionBankProgress } from '../features/questionBank/data/progressStorage';

function read(key: string): any {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** Combine the legacy `vocab-bookmarks` and the `vocab-bookmarks-v2` copy. */
function mergeVocabBookmarks(): Record<string, Record<string, { level: number; ts?: string }>> {
  const a = read('vocab-bookmarks') || {};
  const b = read('vocab-bookmarks-v2') || {};
  // a is keyed by `${deckSlug}|${index}`; re-key by deckSlug -> { index: {...} }.
  const out: Record<string, Record<string, { level: number; ts?: string }>> = {};
  for (const [k, v] of Object.entries(a)) {
    const [deck, idx] = String(k).split('|');
    out[deck] = out[deck] || {};
    out[deck][idx] = (v as { level: number; ts?: string }) || { level: 1 };
  }
  for (const [deck, cards] of Object.entries(b)) {
    out[deck] = out[deck] || {};
    for (const [idx, val] of Object.entries(cards as Record<string, { level: number; ts?: string }>)) {
      out[deck][idx] = val;
    }
  }
  return out;
}

function collectStudyCalendar(): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith('nd-cal-')) {
        const programId = key.slice('nd-cal-'.length);
        const val = read(key);
        if (Array.isArray(val)) out[programId] = val;
      }
    }
  } catch {
    /* ignore */
  }
  return out;
}

function collectUiPrefs(): Record<string, unknown> {
  const prefs: Record<string, unknown> = {};
  const pdfH = Number(localStorage.getItem('mgv.pdfPreviewHeightPx'));
  if (Number.isFinite(pdfH)) prefs.pdfPreviewHeightPx = pdfH;
  const pdfHl = Number(localStorage.getItem('mgv.languageLearning.pdfPreviewHeightPx'));
  if (Number.isFinite(pdfHl)) prefs.pdfPreviewHeightPxLanguage = pdfHl;
  const overlay = read('mgv.pdfPreviewOverlaySize');
  if (overlay && typeof overlay === 'object') prefs.pdfOverlaySize = overlay;
  const theme = localStorage.getItem('theme');
  if (theme) prefs.theme = theme;
  return prefs;
}

/** Build the full namespace map from localStorage. */
export function collectLocalUserState(userId?: string): Record<string, unknown> {
  const studioKey = bookmarkStorageKey(userId || '');
  const studioBookmarks = read(studioKey) || read('studio_bookmarks:guest') || [];
  return {
    vocabBookmarks: mergeVocabBookmarks(),
    vocabEdits: read('vocab-edits') || {},
    vocabDeletes: read('vocab-deletes') || {},
    vocabAdds: read('vocab-adds') || [],
    curriculumStars: read('curriculum:userStars') || {},
    studyCalendar: collectStudyCalendar(),
    studyAssistant: {
      mastery: read('sa-mastery-local') || {},
      reviewLog: read('sa-review-log') || [],
    },
    studioBookmarks,
    dashboardCourses: read('sa-dashboard-courses-v1') || [],
    uiPrefs: collectUiPrefs(),
    questionBankProgress: collectQuestionBankProgress(),
  };
}

/**
 * Namespaces whose endpoint returned a non-retryable status (e.g. 404/405) this
 * session. The `/api/user/data[/:ns]` routes may be absent on a backend that
 * hasn't deployed the user-data hub yet; once we know an endpoint is missing we
 * never hammer it again (avoids 404 spam across mounts / StrictMode re-invokes).
 */
const unavailableNamespaces = new Set<string>();
const UNAVAILABLE_STATUS = new Set([404, 405, 410]);

/**
 * Push each non-empty namespace to the server. Returns how many namespaces were
 * flushed. Failures are swallowed so the UI never blocks on the migration.
 */
export async function flushLocalUserStateToDb(token: string, userId?: string): Promise<{ flushed: number }> {
  if (!token) return { flushed: 0 };
  const state = collectLocalUserState(userId);
  let flushed = 0;
  for (const [ns, value] of Object.entries(state)) {
    const empty =
      value == null ||
      (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) ||
      (Array.isArray(value) && value.length === 0);
    if (empty || unavailableNamespaces.has(ns)) continue;
    try {
      const res = await fetch(`${API_URL}/user/data/${ns}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ value }),
      });
      if (res.ok) {
        flushed += 1;
      } else if (UNAVAILABLE_STATUS.has(res.status)) {
        // Endpoint not deployed on this backend — stop retrying this namespace.
        unavailableNamespaces.add(ns);
      }
    } catch {
      /* network error — non-fatal, but allow a later retry (transient) */
    }
  }
  return { flushed };
}
